/**
 * Public API for PDF Compare. Wraps the Web Worker with a friendly
 * EventEmitter-style interface; falls back to running on the main thread
 * synchronously if the worker isn't available.
 *
 * Usage:
 *   const session = await compareDocuments({ file1, file2 });
 *   session.on('page-text', ({ page, changes }) => ...);
 *   session.on('text-complete', ({ totalChanges }) => ...);
 *   session.on('error', ({ message }) => ...);
 *   session.requestVisualDiff(pageIndex, scale);
 *   session.terminate();
 */

import { diffPageTextItems, computeVisualBboxes } from './compareWorkerCore';

class CompareSession {
  constructor() {
    this._listeners = new Map();
    this._worker = null;
    this._terminated = false;
  }
  on(type, fn) {
    if (!this._listeners.has(type)) this._listeners.set(type, new Set());
    this._listeners.get(type).add(fn);
    return () => this._listeners.get(type)?.delete(fn);
  }
  _emit(type, payload) {
    if (this._terminated) return;
    const set = this._listeners.get(type);
    if (set) for (const fn of set) {
      try { fn(payload); } catch (err) { console.error('CompareSession listener threw', err); }
    }
  }
  requestVisualDiff(page, scale = 1.5) {
    if (this._terminated || !this._worker) return;
    this._worker.postMessage({ type: 'visual-diff', page, scale });
  }
  providePassword(side, password) {
    if (this._terminated || !this._worker) return;
    this._worker.postMessage({ type: 'password', side, password });
  }
  cancel() {
    if (this._terminated || !this._worker) return;
    this._worker.postMessage({ type: 'cancel' });
  }
  terminate() {
    if (this._terminated) return;
    this._terminated = true;
    this._listeners.clear();
    if (this._worker) {
      try { this._worker.terminate(); } catch { /* ignore */ }
      this._worker = null;
    }
  }
}

/**
 * Spin up a CompareSession and start processing the two files.
 *
 * @param {object} args
 * @param {File|Blob} args.file1
 * @param {File|Blob} args.file2
 * @returns {Promise<CompareSession>}
 */
export async function compareDocuments({ file1, file2 }) {
  const session = new CompareSession();
  const buf1 = await file1.arrayBuffer();
  const buf2 = await file2.arrayBuffer();
  // Defensive copies — main thread may reuse the original buffers for
  // rendering, and transfer detaches the originals.
  const buf1Copy = buf1.slice(0);
  const buf2Copy = buf2.slice(0);

  let worker;
  try {
    if (typeof Worker === 'undefined') throw new Error('Worker not supported');
    worker = new Worker(new URL('./compareWorker.js', import.meta.url), { type: 'module' });
  } catch (err) {
    // Synchronous fallback — run the core directly on the main thread.
    runSyncFallback(session, buf1Copy, buf2Copy);
    return session;
  }
  session._worker = worker;

  worker.onmessage = (event) => {
    const msg = event.data;
    session._emit(msg.type, msg);
  };
  worker.onerror = (event) => {
    session._emit('error', { message: event.message || 'Compare worker crashed' });
    session.terminate();
  };

  worker.postMessage(
    { type: 'init', file1: buf1Copy, file2: buf2Copy },
    [buf1Copy, buf2Copy],
  );
  return session;
}

/**
 * Main-thread synchronous fallback. Used when Worker isn't available.
 * Loses parallelism but keeps the same event surface.
 *
 * Note: in this mode visual diff requests are best-effort — they may
 * block the UI thread. Caller should use sparingly.
 */
async function runSyncFallback(session, buf1, buf2) {
  try {
    const pdfjs = await import('pdfjs-dist');
    // Configure the worker URL for main-thread pdfjs use.
    const workerUrl = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl.default;

    const doc1 = await pdfjs.getDocument({ data: buf1 }).promise;
    const doc2 = await pdfjs.getDocument({ data: buf2 }).promise;
    session._emit('loaded', { pageCount1: doc1.numPages, pageCount2: doc2.numPages });

    const maxPages = Math.max(doc1.numPages, doc2.numPages);
    let total = 0;
    for (let i = 0; i < maxPages; i++) {
      const [items1, items2, pageWidth] = await Promise.all([
        i < doc1.numPages ? doc1.getPage(i + 1).then((p) => p.getTextContent()).then((c) => c.items) : Promise.resolve([]),
        i < doc2.numPages ? doc2.getPage(i + 1).then((p) => p.getTextContent()).then((c) => c.items) : Promise.resolve([]),
        i < doc1.numPages ? doc1.getPage(i + 1).then((p) => p.view[2] - p.view[0]) : Promise.resolve(612),
      ]);
      const { changes, columnCount } = diffPageTextItems(items1, items2, i, { pageWidth });
      total += changes.length;
      session._emit('page-text', { page: i, changes, columnCount });
    }
    session._emit('text-complete', { totalChanges: total });

    // Visual diff requests fall through to the same code path as the worker
    // — but rendered on a regular <canvas>. Defer implementation: the
    // happy path is worker-mode; this exists so text diff at least works
    // on any browser.
    session.requestVisualDiff = () => {
      session._emit('visual-diff-result', { page: -1, bboxes: [], scale: 1 });
    };
  } catch (err) {
    session._emit('error', { message: String(err?.message || err) });
  }
}
